import React, { useState } from "react";
import { useDietaryReports } from "../hooks/useDietaryReports";
import EditDietDialog from "./edit-diet-dialog";
import { DietReport } from "src/redux/types/_customer.type";
import { PencilLine } from "lucide-react";

const ViewDietaryReport = ({ memberId }: { memberId: number }) => {
  const { data: reports = [], isPending } = useDietaryReports(memberId!);

  const [editingReport, setEditingReport] = useState<DietReport | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  if (isPending) {
    return (
      <div className="w-full py-12 flex justify-center items-center">
        <p className="p-4 text-center flex gap-3 items-center">
          <span>Loading Dietary Report </span>
          <span className="loading loading-dots loading-md"></span>
        </p>
      </div>
    );
  }

  return (
    <div className="w-fit mx-auto mt-8 p-6 rounded-lg border">
      <div>
        <h2 className="text-lg text-center mb-3">Dietary Recall Parameters</h2>
      </div>
      <table className="min-w-[900px] table-auto border-collapse border border-gray-400 text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th
              className="border border-gray-300 px-2 py-1 sticky left-0 bg-gray-100 z-10"
              colSpan={2}
            >
              Dietary Recalls for last 5 days
            </th>
            {[0, 1, 2, 3, 4].map((i) => {
              const report = reports[i];
              return (
                <React.Fragment key={i}>
                  <th className="border border-gray-300 px-2 py-1 whitespace-nowrap">
                    Time <br />({report?.day ? `Day-${report.day}` : `Day--`})
                  </th>
                  <th className="border border-gray-300 px-2 py-1">
                    <div className="flex items-center justify-center gap-1">
                      {report?.day ? `Day-${report.day}` : `Day--`}
                      {report && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingReport(report);
                            setIsOpen(!isOpen);
                          }}
                          className="text-muted-foreground hover:text-foreground border p-1 rounded-full"
                          aria-label={`Edit Day ${report.day}`}
                        >
                          <PencilLine className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </th>
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {[
            [
              "Morning Break Fast",
              "morning_meal_fast_time",
              "morning_meal_fast",
            ],
            ["Lunch at Noon", "lunch_time", "lunch"],
            ["Evening Snacks", "evening_snacks_time", "evening_snacks"],
            ["Dinner Night", "dinner_time", "dinner"],
            [
              "Anything in between",
              "anything_between_time",
              "anything_between",
            ],
          ].map(([meal, timeField, dataField], i) => (
            <tr key={i}>
              <td
                className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10"
                colSpan={2}
              >
                {meal}
              </td>

              {[0, 1, 2, 3, 4].map((dayIndex) => {
                const report = reports[dayIndex];
                return (
                  <React.Fragment key={dayIndex}>
                    <td className="border border-gray-300 px-2 py-1">
                      {report?.[timeField] ?? "--"}
                    </td>
                    <td className="border border-gray-300 px-2 py-1">
                      {report?.[dataField] ?? "--"}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {editingReport && (
        <EditDietDialog
          report={editingReport}
          memberId={memberId}
          open={isOpen}
          close={() => {
            setIsOpen(!isOpen);
            setEditingReport(null);
          }}
        />
      )}
    </div>
  );
};

export default ViewDietaryReport;
